/**
 * Adapter factory — selects the correct implementation tier at runtime.
 *
 * Tier selection:
 *   NEXT_PUBLIC_DEPLOYMENT_TIER=cloud   → Supabase adapters  (default)
 *   NEXT_PUBLIC_DEPLOYMENT_TIER=self-hosted → SelfHosted adapters
 *
 * All application code should import adapters from this module, never
 * from the individual implementation files.
 */

import type {
  DatabaseAdapter,
  AuthAdapter,
  RealtimeAdapter,
  EmailAdapter,
} from './interfaces';

// Supabase implementations
import { SupabaseDatabaseAdapter } from './supabase/SupabaseDatabaseAdapter';
import { SupabaseAuthAdapter } from './supabase/SupabaseAuthAdapter';
import { SupabaseRealtimeAdapter } from './supabase/SupabaseRealtimeAdapter';
import { SupabaseEmailAdapter } from './supabase/SupabaseEmailAdapter';

// Self-hosted implementations
import { SelfHostedDatabaseAdapter } from './self-hosted/SelfHostedDatabaseAdapter';
import { SelfHostedAuthAdapter } from './self-hosted/SelfHostedAuthAdapter';
import { SelfHostedRealtimeAdapter } from './self-hosted/SelfHostedRealtimeAdapter';
import { RedisRealtimeAdapter } from './self-hosted/RedisRealtimeAdapter';
import { SelfHostedEmailAdapter } from './self-hosted/SelfHostedEmailAdapter';

// ---------------------------------------------------------------------------
// Tier detection
// ---------------------------------------------------------------------------

type Tier = 'cloud' | 'self-hosted';

function getTier(): Tier {
  const raw = process.env.NEXT_PUBLIC_DEPLOYMENT_TIER ?? 'cloud';
  return raw === 'self-hosted' ? 'self-hosted' : 'cloud';
}

// ---------------------------------------------------------------------------
// Singleton instances (one per adapter per process)
// ---------------------------------------------------------------------------

let _db: DatabaseAdapter | null = null;
let _auth: AuthAdapter | null = null;
let _realtime: RealtimeAdapter | null = null;
let _email: EmailAdapter | null = null;

export function getDatabase(): DatabaseAdapter {
  if (!_db) {
    _db = getTier() === 'self-hosted'
      ? new SelfHostedDatabaseAdapter()
      : new SupabaseDatabaseAdapter();
  }
  return _db;
}

export function getAuth(): AuthAdapter {
  if (!_auth) {
    _auth = getTier() === 'self-hosted'
      ? new SelfHostedAuthAdapter()
      : new SupabaseAuthAdapter();
  }
  return _auth;
}

export function getRealtime(): RealtimeAdapter {
  if (!_realtime) {
    if (getTier() === 'self-hosted') {
      // Use Redis pub/sub adapter for high-throughput self-hosted tier.
      // Falls back to WebSocket adapter when NEXT_PUBLIC_REALTIME_BACKEND=websocket.
      const backend = process.env.NEXT_PUBLIC_REALTIME_BACKEND ?? 'redis';
      _realtime = backend === 'websocket'
        ? new SelfHostedRealtimeAdapter()
        : new RedisRealtimeAdapter();
    } else {
      _realtime = new SupabaseRealtimeAdapter();
    }
  }
  return _realtime;
}

export function getEmail(): EmailAdapter {
  if (!_email) {
    _email = getTier() === 'self-hosted'
      ? new SelfHostedEmailAdapter()
      : new SupabaseEmailAdapter();
  }
  return _email;
}

/**
 * Reset all singletons — useful in tests or when switching tiers at runtime.
 */
export function resetAdapters() {
  _db = null;
  _auth = null;
  _realtime = null;
  _email = null;
}

// ---------------------------------------------------------------------------
// Re-export interfaces for convenience
// ---------------------------------------------------------------------------
export type {
  DatabaseAdapter,
  AuthAdapter,
  RealtimeAdapter,
  EmailAdapter,
} from './interfaces';
